/**
 * API Client centralizzato per comunicare con il backend
 * Priorità env vars: REACT_APP_API_BASE_URL (esistente) > REACT_APP_API_URL (nuovo) > fallback dev
 */

// 1. Seleziona base URL con priorità corretta
let BASE_URL = process.env.REACT_APP_API_BASE_URL?.trim() || 
               process.env.REACT_APP_API_URL?.trim() || 
               "";

// 2. Fallback dev solo se nessuna env è configurata
if (!BASE_URL && process.env.NODE_ENV === "development") {
  BASE_URL = window.location.origin;
  console.warn("⚠️ [API Client] Nessuna env API configurata, uso fallback:", BASE_URL);
}

// 3. Normalizza: rimuovi trailing slash
BASE_URL = BASE_URL.replace(/\/$/, "");

// 4. Costruisci API base (una sola volta /api)
const API_BASE_URL = `${BASE_URL}/api`;

// 5. Debug logging SOLO in dev
if (process.env.NODE_ENV === "development") {
  console.log("[API Client] BASE_URL:", BASE_URL);
  console.log("[API Client] API_BASE_URL:", API_BASE_URL);
}

class ApiError extends Error {
  constructor(message, status, data) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.data = data;
  }
}

/**
 * Funzione helper per fare richieste HTTP
 * @param {string} endpoint - Endpoint relativo (es: '/esg/requests')
 * @param {object} options - Opzioni fetch (method, headers, body, etc.)
 * @returns {Promise<any>}
 */
async function apiRequest(endpoint, options = {}) {
  if (!BASE_URL) {
    throw new ApiError("REACT_APP_API_BASE_URL o REACT_APP_API_URL non configurato", 0, null);
  }

  // URL finale: API_BASE_URL già include /api, endpoint parte da /
  const url = `${API_BASE_URL}${endpoint}`;
  
  // Logging sempre attivo per debug (anche in prod)
  console.log(`[API Client] ${options.method || 'GET'} ${url}`);
  if (options.headers?.['x-admin-key']) {
    console.log('[API Client] Admin request with key');
  }
  
  const config = {
    ...options,
    headers: {
      "Content-Type": "application/json",
      ...options.headers,
    },
  };

  try {
    const response = await fetch(url, config);
    
    let data;
    const contentType = response.headers.get("content-type");
    if (contentType && contentType.includes("application/json")) {
      data = await response.json();
    } else {
      data = await response.text();
    }

    if (!response.ok) {
      let errorMessage = data?.error || data?.message || `HTTP ${response.status}`;
      
      // Gestione errori specifici per ESG
      if (endpoint.includes('/esg/requests')) {
        if (response.status === 404) {
          // 404: La richiesta ESG non esiste (ID non valido o scaduto)
          errorMessage = `Richiesta non trovata: L'ID della richiesta potrebbe non essere valido o non esiste nel database.`;
          console.error('[API Client] 404 - Richiesta ESG non trovata:', url);
          console.error('[API Client] ID della richiesta non valido o scaduto');
        } else if (response.status >= 500) {
          // 500+: Problema dal lato backend
          errorMessage = `Problema del backend: Il server ha riscontrato un errore. Riprova più tardi.`;
          console.error('[API Client] Errore server su endpoint ESG:', url, response.status);
        }
      }
      
      throw new ApiError(errorMessage, response.status, data);
    }

    return data;
  } catch (error) {
    if (error instanceof ApiError) {
      throw error;
    }
    
    // Errori di rete o parsing
    throw new ApiError(
      error.message || "Errore di rete",
      0,
      null
    );
  }
}

// Removed duplicate export - will export all at the end of file

/**
 * API per il questionario ESG (utente)
 */
export const esgApi = {
  /**
   * Ottiene URL pre-firmato per upload file
   * @param {object} fileInfo - { fileName, contentType, size, questionKey }
   * @returns {Promise<{uploadUrl: string, storageKey: string}>}
   */
  async getPresignedUploadUrl(fileInfo) {
    return apiRequest("/esg/presign-upload", {
      method: "POST",
      body: JSON.stringify(fileInfo),
    });
  },

  /**
   * Upload diretto del file a R2
   * @param {string} uploadUrl - URL pre-firmato
   * @param {File} file - File da uploadare
   * @param {function} onProgress - Callback per progresso (0-100)
   * @returns {Promise<void>}
   */
  async uploadFileToR2(uploadUrl, file, onProgress) {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();

      // Progress tracking
      if (onProgress) {
        xhr.upload.addEventListener("progress", (e) => {
          if (e.lengthComputable) {
            const percentComplete = Math.round((e.loaded / e.total) * 100);
            onProgress(percentComplete);
          }
        });
      }

      xhr.addEventListener("load", () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          resolve();
        } else {
          reject(new Error(`Upload fallito: ${xhr.status}`));
        }
      });

      xhr.addEventListener("error", () => {
        reject(new Error("Errore di rete durante upload"));
      });

      xhr.addEventListener("abort", () => {
        reject(new Error("Upload annullato"));
      });

      xhr.open("PUT", uploadUrl);
      xhr.setRequestHeader("Content-Type", file.type);
      xhr.send(file);
    });
  },

  /**
   * Invia la richiesta ESG completa
   * @param {object} payload - { companyName, contactEmail, answers, attachments }
   * @returns {Promise<{id: string, message: string}>}
   */
  async submitRequest(payload) {
    // Trasforma answers da oggetto a array [{ questionKey, answer, description }]
    let processedPayload = { ...payload };
    
    if (payload.answers && typeof payload.answers === 'object' && !Array.isArray(payload.answers)) {
      // Converti oggetto in array
      processedPayload.answers = Object.entries(payload.answers).map(([questionKey, value]) => {
        // Se il valore è un oggetto con answer e description, usalo
        if (value && typeof value === 'object' && 'answer' in value) {
          return {
            questionKey,
            answer: value.answer,
            description: value.description || null
          };
        }
        // Altrimenti usa il valore direttamente
        return {
          questionKey,
          answer: value,
          description: null
        };
      });
    }

    // Retrocompatibilità backend: alcuni backend usano contactEmail/contactPhone
    if (!processedPayload.contactEmail && processedPayload.email) {
      processedPayload.contactEmail = processedPayload.email;
    }
    if (!processedPayload.email && processedPayload.contactEmail) {
      processedPayload.email = processedPayload.contactEmail;
    }
    if (!processedPayload.contactPhone && processedPayload.phone) {
      processedPayload.contactPhone = processedPayload.phone;
    }
    if (!processedPayload.phone && processedPayload.contactPhone) {
      processedPayload.phone = processedPayload.contactPhone;
    }

    const hasLogoAttachment = Array.isArray(processedPayload.attachments)
      && processedPayload.attachments.some((att) => att?.questionKey === 'q11_company_logo');

    try {
      return await apiRequest("/esg/requests", {
        method: "POST",
        body: JSON.stringify(processedPayload),
      });
    } catch (error) {
      // Fallback mirato: se backend non supporta ancora q11_company_logo,
      // ritenta senza logo (campo opzionale) mantenendo invariati gli altri allegati.
      if (
        error instanceof ApiError
        && error.status === 400
        && hasLogoAttachment
      ) {
        const retryPayload = {
          ...processedPayload,
          attachments: processedPayload.attachments.filter((att) => att?.questionKey !== 'q11_company_logo'),
        };

        console.warn('[API Client] Retry submit without optional logo attachment (q11_company_logo) after 400');

        return apiRequest("/esg/requests", {
          method: "POST",
          body: JSON.stringify(retryPayload),
        });
      }

      throw error;
    }
  },

  /**
   * Ottiene lo stato di una richiesta ESG
   * @param {string} requestId - ID della richiesta
   * @returns {Promise<{id: string, status: string}>}
   */
  async getRequestStatus(requestId) {
    return apiRequest(`/esg/requests/${requestId}/status`, {
      method: "GET",
    });
  },

  /**
   * Valida che una richiesta ESG esista nel backend
   * @param {string} requestId - ID della richiesta
   * @returns {Promise<boolean>} - true se esiste, false se 404 o errore
   */
  async validateRequestId(requestId) {
    try {
      if (!requestId || requestId.trim() === "") {
        console.warn('[API Client] ID richiesta vuoto');
        return false;
      }

      const response = await this.getRequestStatus(requestId);
      
      if (response && response.id) {
        console.log('[API Client] ✅ ID richiesta validato:', requestId);
        return true;
      }
      
      return false;
    } catch (error) {
      if (error instanceof ApiError && error.status === 404) {
        console.warn('[API Client] ⚠️ Richiesta non trovata - ID non valido:', requestId);
        return false;
      }
      
      console.error('[API Client] Errore durante validazione ID:', error);
      return false;
    }
  },
};

/**
 * API per l'admin panel
 */
export const adminApi = {
  /**
   * Verifica l'admin key senza caricare dati
   * @param {string} adminKey - Chiave admin da verificare
   * @param {string} walletAddress - Indirizzo wallet dell'admin (opzionale)
   * @returns {Promise<{success: boolean, message: string}>}
   */
  async verifyAdminKey(adminKey, walletAddress = null) {
    const headers = {
      "x-admin-key": adminKey,
    };
    
    if (walletAddress) {
      headers["x-wallet-address"] = walletAddress;
    }
    
    return apiRequest("/admin/verify", {
      method: "POST",
      headers,
    });
  },

  /**
   * Ottiene la lista delle richieste ESG
   * @param {object} params - { status, q, page, pageSize }
   * @param {string} adminKey - Chiave admin per autenticazione
   * @param {string} walletAddress - Indirizzo wallet dell'admin
   * @returns {Promise<{requests: Array, total: number, page: number, pageSize: number}>}
   */
  async getRequests(params = {}, adminKey, walletAddress = null) {
    const searchParams = new URLSearchParams();
    if (params.status) searchParams.append("status", params.status);
    if (params.q) searchParams.append("q", params.q);
    if (params.page) searchParams.append("page", params.page);
    if (params.pageSize) searchParams.append("pageSize", params.pageSize);

    const query = searchParams.toString();
    const endpoint = `/admin/esg/requests${query ? `?${query}` : ""}`;

    const headers = {
      "x-admin-key": adminKey,
    };
    
    if (walletAddress) {
      headers["x-wallet-address"] = walletAddress;
    }

    return apiRequest(endpoint, {
      headers,
    });
  },

  /**
   * Ottiene il dettaglio di una richiesta ESG
   * @param {string} id - ID della richiesta
   * @param {string} adminKey - Chiave admin
   * @param {string} walletAddress - Indirizzo wallet dell'admin
   * @returns {Promise<object>}
   */
  async getRequestDetail(id, adminKey, walletAddress = null) {
    const headers = {
      "x-admin-key": adminKey,
    };
    
    if (walletAddress) {
      headers["x-wallet-address"] = walletAddress;
    }
    
    return apiRequest(`/admin/esg/requests/${id}`, {
      headers,
    });
  },

  /**
   * Ottiene URL pre-firmato per download allegato
   * @param {string} attachmentId - ID dell'allegato
   * @param {string} adminKey - Chiave admin
   * @param {string} walletAddress - Indirizzo wallet dell'admin
   * @returns {Promise<{downloadUrl: string}>}
   */
  async getPresignedDownloadUrl(attachmentId, adminKey, walletAddress = null) {
    const headers = {
      "x-admin-key": adminKey,
    };
    
    if (walletAddress) {
      headers["x-wallet-address"] = walletAddress;
    }
    
    return apiRequest(`/admin/esg/attachments/${attachmentId}/presign-download`, {
      headers,
    });
  },

  /**
   * Ottiene URL per download ZIP di tutti gli allegati di una richiesta
   * @param {string} requestId - ID della richiesta
   * @param {string} adminKey - Chiave admin
   * @returns {string} URL per il download
   */
  getZipDownloadUrl(requestId, adminKey) {
    const params = new URLSearchParams({ key: adminKey });
    return `${API_BASE_URL}/api/admin/esg/requests/${requestId}/download-zip?${params}`;
  },

  /**
   * Approva una richiesta ESG
   * @param {string} id - ID della richiesta
   * @param {string} adminKey - Chiave admin
   * @param {string} walletAddress - Indirizzo wallet dell'admin
   * @returns {Promise<object>}
   */
  async approveRequest(id, adminKey, walletAddress = null) {
    const headers = {
      "x-admin-key": adminKey,
    };
    
    if (walletAddress) {
      headers["x-wallet-address"] = walletAddress;
    }
    
    return apiRequest(`/admin/esg/requests/${id}/approve`, {
      method: "POST",
      headers,
    });
  },

  /**
   * Rifiuta una richiesta ESG
   * @param {string} id - ID della richiesta
   * @param {string} reason - Motivo del rifiuto (OBBLIGATORIO)
   * @param {string} adminKey - Chiave admin
   * @param {string} walletAddress - Indirizzo wallet dell'admin
   * @returns {Promise<object>}
   */
  async rejectRequest(id, reason, adminKey, walletAddress = null) {
    const headers = {
      "x-admin-key": adminKey,
    };
    
    if (walletAddress) {
      headers["x-wallet-address"] = walletAddress;
    }
    
    return apiRequest(`/admin/esg/requests/${id}/reject`, {
      method: "POST",
      headers,
      body: JSON.stringify({ reason }),
    });
  },

  /**
   * Richiede informazioni aggiuntive per una richiesta ESG
   * @param {string} id - ID della richiesta
   * @param {string} message - Messaggio con richiesta informazioni (OBBLIGATORIO)
   * @param {string} adminKey - Chiave admin
   * @param {string} walletAddress - Indirizzo wallet dell'admin
   * @returns {Promise<object>}
   */
  async requestMoreInfo(id, message, adminKey, walletAddress = null) {
    const headers = {
      "x-admin-key": adminKey,
    };
    
    if (walletAddress) {
      headers["x-wallet-address"] = walletAddress;
    }
    
    return apiRequest(`/admin/esg/requests/${id}/need-info`, {
      method: "POST",
      headers,
      body: JSON.stringify({ message }),
    });
  },

  /**
   * Aggiunge un commento a una richiesta ESG
   * @param {string} requestId - ID della richiesta
   * @param {string} note - Testo del commento
   * @param {string} adminKey - Chiave admin
   * @param {string} walletAddress - Indirizzo wallet dell'admin
   * @returns {Promise<object>}
   */
  async addComment(requestId, note, adminKey, walletAddress = null) {
    const headers = {
      "x-api-key": "APT-ESG-2024-SECURE-KEY",
    };
    
    if (walletAddress) {
      headers["x-wallet-address"] = walletAddress;
    }
    
    return apiRequest(`/admin/esg/requests/${requestId}/comment`, {
      method: "POST",
      headers,
      body: JSON.stringify({ note, action: "COMMENT" }),
    });
  },

  /**
   * Elimina una richiesta ESG
   * @param {string} id - ID della richiesta
   * @param {string} adminKey - Chiave admin
   * @param {string} walletAddress - Indirizzo wallet dell'admin
   * @returns {Promise<object>}
   */
  async deleteRequest(id, adminKey, walletAddress = null) {
    const headers = {
      "x-admin-key": adminKey,
    };
    
    if (walletAddress) {
      headers["x-wallet-address"] = walletAddress;
    }
    
    return apiRequest(`/admin/esg/requests/${id}`, {
      method: "DELETE",
      headers,
    });
  },

  /**
   * Aggiorna lo stato di una richiesta ESG
   * @param {string} id - ID della richiesta
   * @param {string} status - Nuovo status (pending, approved, rejected, need_info)
   * @param {string} notes - Note opzionali
   * @param {string} adminKey - Chiave admin
   * @param {string} walletAddress - Indirizzo wallet dell'admin
   * @returns {Promise<object>}
   */
  async updateRequestStatus(id, status, notes, adminKey, walletAddress = null) {
    const headers = {
      "x-admin-key": adminKey,
    };
    
    if (walletAddress) {
      headers["x-wallet-address"] = walletAddress;
    }
    
    return apiRequest(`/admin/esg/requests/${id}/status`, {
      method: "PATCH",
      headers,
      body: JSON.stringify({ status, notes }),
    });
  },

  /**
   * Prepara la certificazione (step 1) - USA PROXY SICURO
   * Chiama un endpoint proxy del backend che gestisce le credenziali admin server-side
   * @param {string} requestId - ID della richiesta ESG
   * @param {string} adminKey - Chiave admin per autenticazione proxy
   * @param {string} walletAddress - Indirizzo wallet dell'admin per autenticazione proxy
   * @returns {Promise<{success: boolean, certificationData: object}>}
   */
  async prepareCertification(requestId, adminKey, walletAddress = null) {
    // Chiama il proxy backend che gestisce le credenziali admin server-side
    // Il backend verificherà adminKey/walletAddress e poi chiamerà il servizio ESG
    const headers = {
      "Content-Type": "application/json",
      "x-admin-key": adminKey,
    };
    
    if (walletAddress) {
      headers["x-wallet-address"] = walletAddress;
    }
    
    return apiRequest(`/admin/esg/certifications/prepare-proxy`, {
      method: "POST",
      headers,
      body: JSON.stringify({ submissionId: requestId }),
    });
  },

  /**
   * Conferma la certificazione (step 2 - dopo tx on-chain)
   * @param {string} requestId - ID della richiesta ESG
   * @param {string} txHash - Hash della transazione blockchain
   * @param {string} adminKey - Chiave admin
   * @param {string} walletAddress - Indirizzo wallet dell'admin
   * @returns {Promise<{success: boolean, certification: object}>}
   */
  async confirmCertification(requestId, txHash, adminKey, walletAddress = null) {
    const headers = {
      "x-admin-key": adminKey,
    };
    
    if (walletAddress) {
      headers["x-wallet-address"] = walletAddress;
    }
    
    return apiRequest(`/admin/esg/certifications/confirm`, {
      method: "POST",
      headers,
      body: JSON.stringify({ requestId, txHash }),
    });
  },

  /**
   * Conferma la certificazione OFF-CHAIN (senza transazione blockchain)
   * Per aziende senza wallet o certificazioni interne
   * @param {string} requestId - ID della richiesta ESG
   * @param {string} reportCID - CID del report JSON su IPFS
   * @param {string} pdfCID - CID del PDF su IPFS
   * @param {string} adminKey - Chiave admin
   * @param {string} walletAddress - Indirizzo wallet dell'admin
   * @returns {Promise<{success: boolean, certification: object}>}
   */
  async confirmCertificationOffChain(requestId, reportCID, pdfCID, adminKey, walletAddress = null) {
    const headers = {
      "x-admin-key": adminKey,
    };
    
    if (walletAddress) {
      headers["x-wallet-address"] = walletAddress;
    }
    
    return apiRequest(`/admin/esg/certifications/confirm-offchain`, {
      method: "POST",
      headers,
      body: JSON.stringify({ requestId, reportCID, pdfCID }),
    });
  },

  /**
   * Revoca la certificazione (dopo tx on-chain di decertify)
   * @param {string} requestId - ID della richiesta ESG
   * @param {string} txHash - Hash della transazione blockchain di revoca
   * @param {string} reason - Motivo della revoca
   * @param {string} adminKey - Chiave admin
   * @param {string} walletAddress - Indirizzo wallet dell'admin
   * @returns {Promise<{success: boolean, certificationStatus: string, revokedTxHash: string, revokedAt: string, blockchainExplorerUrl: string}>}
   */
  async revokeCertification(requestId, txHash, reason, adminKey, walletAddress = null) {
    const headers = {
      "x-admin-key": adminKey,
    };
    
    if (walletAddress) {
      headers["x-wallet-address"] = walletAddress;
    }
    
    return apiRequest(`/admin/esg/certifications/revoke`, {
      method: "POST",
      headers,
      body: JSON.stringify({ requestId, txHash, reason }),
    });
  },

  /**
   * Ottiene lo scoring per una submission
   * @param {string} submissionId - ID della submission
   * @param {string} adminKey - Chiave admin
   * @param {string} walletAddress - Indirizzo wallet dell'admin
   * @returns {Promise<object>}
   */
  async getScoring(submissionId, adminKey, walletAddress = null) {
    const headers = {
      "x-admin-key": adminKey,
    };
    
    if (walletAddress) {
      headers["x-wallet-address"] = walletAddress;
    }
    
    return apiRequest(`/admin/esg/scoring/${submissionId}`, {
      method: "GET",
      headers,
    });
  },

  /**
   * Salva lo scoring per una submission
   * @param {object} scoringData - { submissionId, scoreVersion, byQuestion }
   * @param {string} adminKey - Chiave admin
   * @param {string} walletAddress - Indirizzo wallet dell'admin
   * @returns {Promise<object>}
   */
  async saveScoring(scoringData, adminKey, walletAddress = null) {
    const headers = {
      "x-admin-key": adminKey,
    };
    
    if (walletAddress) {
      headers["x-wallet-address"] = walletAddress;
    }
    
    return apiRequest(`/admin/esg/scoring/save`, {
      method: "POST",
      headers,
      body: JSON.stringify(scoringData),
    });
  },

  /**
   * Ottiene le statistiche della dashboard ESG (badge, livelli, evidenze)
   * @param {string} adminKey - Chiave admin
   * @param {string} walletAddress - Indirizzo wallet dell'admin
   * @returns {Promise<{levelDistribution, topBadges, ipfsStats, evidenceStats}>}
   */
  async getEsgDashboardStats(adminKey, walletAddress = null) {
    const headers = {
      "x-admin-key": adminKey,
    };
    
    if (walletAddress) {
      headers["x-wallet-address"] = walletAddress;
    }
    
    return apiRequest(`/admin/esg/dashboard/stats`, {
      method: "GET",
      headers,
    });
  },
};

/**
 * API pubblica per il monitoraggio della liquidità (no auth richiesta)
 */
export const liquidityApi = {
  /**
   * Stato globale del programma di liquidità
   * @returns {Promise<{walletLimit, weeklyWarning, weeklyMax, weeklyUsed, weeklyRemaining, weeklyUsedPercent, windowResetAt, isPaused, pausedAt, pausedReason}>}
   */
  async getStatus() {
    return apiRequest("/liquidity/status", { method: "GET" });
  },

  /**
   * Info liquidità per un wallet specifico
   * @param {string} walletAddress
   * @returns {Promise<{wallet, totalEthProvided, weeklyEthProvided, walletLimit, walletRemaining, isFlagged, flagInfo}>}
   */
  async getWalletInfo(walletAddress) {
    return apiRequest(`/liquidity/wallet/${walletAddress}`, { method: "GET" });
  },
};

/**
 * API admin per il monitoraggio della liquidità (richiede x-admin-key)
 */
export const adminLiquidityApi = {
  /**
   * Stato completo del programma con metadati pausa
   */
  async getState(adminKey) {
    return apiRequest("/admin/liquidity/state", {
      method: "GET",
      headers: { "x-admin-key": adminKey },
    });
  },

  /**
   * Lista incidenti
   * @param {object} params - { type, resolved, limit, offset }
   */
  async getIncidents(adminKey, params = {}) {
    const q = new URLSearchParams();
    if (params.type) q.append("type", params.type);
    if (params.resolved !== undefined) q.append("resolved", params.resolved);
    if (params.limit) q.append("limit", params.limit);
    if (params.offset) q.append("offset", params.offset);
    const qs = q.toString();
    return apiRequest(`/admin/liquidity/incidents${qs ? `?${qs}` : ""}`, {
      method: "GET",
      headers: { "x-admin-key": adminKey },
    });
  },

  /**
   * Wallet segnalati per anomalie
   */
  async getFlaggedWallets(adminKey, params = {}) {
    const q = new URLSearchParams();
    if (params.reviewed !== undefined) q.append("reviewed", params.reviewed);
    if (params.limit) q.append("limit", params.limit);
    if (params.offset) q.append("offset", params.offset);
    const qs = q.toString();
    return apiRequest(`/admin/liquidity/flagged-wallets${qs ? `?${qs}` : ""}`, {
      method: "GET",
      headers: { "x-admin-key": adminKey },
    });
  },

  /**
   * Statistiche settimanali + storico giornaliero 30gg
   */
  async getWeeklyStats(adminKey) {
    return apiRequest("/admin/liquidity/weekly-stats", {
      method: "GET",
      headers: { "x-admin-key": adminKey },
    });
  },

  /**
   * Riattiva il programma dopo una pausa
   */
  async resume(adminKey) {
    return apiRequest("/admin/liquidity/resume", {
      method: "POST",
      headers: { "x-admin-key": adminKey },
    });
  },

  /**
   * Segna un incidente come risolto
   * @param {number} id
   */
  async resolveIncident(adminKey, id) {
    return apiRequest("/admin/liquidity/resolve-incident", {
      method: "POST",
      headers: { "x-admin-key": adminKey },
      body: JSON.stringify({ id }),
    });
  },

  /**
   * Segna un wallet segnalato come revisionato
   * @param {string} wallet
   * @param {string} reviewedBy
   */
  async reviewWallet(adminKey, wallet, reviewedBy) {
    return apiRequest("/admin/liquidity/review-wallet", {
      method: "POST",
      headers: { "x-admin-key": adminKey },
      body: JSON.stringify({ wallet, reviewedBy }),
    });
  },
};

export { ApiError, apiRequest, API_BASE_URL };
